/**
 * Subscription React Query patch helpers (optimistic double_box_dates).
 */
import type { QueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { subscriptionKeys } from './keys';
import type { SubscriptionData, SubscriptionDataResponse } from './types';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';

let deferred_subscription_refetch_timer: ReturnType<typeof setTimeout> | null = null;

function subscriptionOwnsDate(sub: SubscriptionData, date_ascii: string): boolean {
  const m = moment(date_ascii, 'YYYY-MM-DD', true);
  if (!m.isValid()) {
    return false;
  }
  const start = moment(sub.start_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  const end = moment(sub.end_date, ['YYYY-MM-DD', 'DD MMMM, YYYY'], true);
  if (!start.isValid() || !end.isValid()) {
    return false;
  }
  return m.isBetween(start, end, 'day', '[]');
}

/** Patch `double_box_dates` + `dayConfigMap.isDoubleBox` in RQ without a network refetch. */
export function patchSubscriptionDoubleBoxDateInCache(
  query_client: QueryClient,
  customer_id: number,
  date_raw: string,
  add: boolean,
): void {
  const date_ascii = ensureAsciiDateString(date_raw) || date_raw;
  const key = subscriptionKeys.detail(customer_id);
  const prev = query_client.getQueryData(key) as SubscriptionDataResponse | undefined;
  if (!prev) {
    return;
  }

  const owner_id = prev.dayConfigMap[date_ascii]?.selectedSubscriptionID;

  const subscriptions = prev.subscriptions.map(sub => {
    const is_owner =
      typeof owner_id === 'number' ? sub.id === owner_id : subscriptionOwnsDate(sub, date_ascii);
    if (!is_owner) {
      return sub;
    }
    const date_set = new Set((sub.double_box_dates ?? []).map(d => ensureAsciiDateString(d) || d));
    if (add) {
      date_set.add(date_ascii);
    } else {
      date_set.delete(date_ascii);
    }
    return { ...sub, double_box_dates: Array.from(date_set) };
  });

  const day_config_map = { ...prev.dayConfigMap };
  const existing_day = day_config_map[date_ascii];
  if (existing_day) {
    day_config_map[date_ascii] = { ...existing_day, isDoubleBox: add };
  }

  query_client.setQueryData(key, {
    ...prev,
    subscriptions,
    dayConfigMap: day_config_map,
  });
}

/** Debounced subscription refetch (e.g. after optimistic patch). */
export function scheduleDeferredSubscriptionRefetch(refetch: () => Promise<unknown>, delay_ms = 30_000): void {
  if (deferred_subscription_refetch_timer != null) {
    clearTimeout(deferred_subscription_refetch_timer);
  }
  deferred_subscription_refetch_timer = setTimeout(() => {
    deferred_subscription_refetch_timer = null;
    void refetch();
  }, delay_ms);
}

export function cancelDeferredSubscriptionRefetch(): void {
  if (deferred_subscription_refetch_timer != null) {
    clearTimeout(deferred_subscription_refetch_timer);
    deferred_subscription_refetch_timer = null;
  }
}
